import { TrainingInitQuery } from '~/data';
import {
  Chapter,
  CompanyContact,
  Policy,
  Sequence
} from './navigation/NavigationSlice';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { createSupportedLanguages } from './settings/createSupportedLanguages';
import { t } from 'ttag';

/**
 * Utility function to process the GraphQL data into something a little more usable by the UI.
 * In particular, it flattens the tree of chapters and sequences into an array.
 * @param lt LearnerTraining from the GraphQL query
 * @returns Zustand store data
 */
export function processData(data: TrainingInitQuery) {
  const items: Array<Sequence | Chapter> = [];
  const { learnerTraining: lt, me: user } = data;
  let highestUnlockedPosition = -1;

  if (!lt.training.chapters) {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: t`An unexpected error occurred.`
    });
  }

  lt.training.chapters?.forEach((chapter, indexPosition) => {
    // Add each chapter to the list of items
    items.push({
      kind: 'chapter',
      id: chapter.id,
      title: chapter.title,
      duration: chapter.duration,
      position: (indexPosition + 1) * 10000,
      sequenceCount: chapter.chapterSequences.length
    });

    // Add each sequence of the chapter to the same list
    chapter.chapterSequences.forEach(
      ({
        id: chapterSequenceId,
        position,
        sequenceId,
        title,
        duration,
        type,
        icon
      }) => {
        const item = {
          kind: 'sequence',
          id: sequenceId,
          position: (indexPosition + 1) * 10000 + (position || 0),
          chapterId: chapter.id,
          chapterSequenceId,
          duration: duration ? Math.round(duration / 6) / 10 : 0,
          type,
          title: title || '',
          icon: icon || ''
        };
        items.push(item as Sequence);

        if (item.chapterSequenceId === lt.chapterSequenceId) {
          // This is how we get the position of the last visited item
          highestUnlockedPosition = item.position;
        }
      }
    );
  });

  if (user.company) {
    const root = document.documentElement;

    if (user.company.brandColorDark) {
      root.style.setProperty('--color-accent-dk', user.company.brandColorDark);
    }

    if (user.company.brandColorLight) {
      root.style.setProperty('--color-accent-lt', user.company.brandColorLight);
    }
  }

  //we can't continue if we don't have chapterSequenceId
  if (lt.chapterSequenceId === '') {
    document.location.reload();
  }

  return {
    // Navigation
    learnerTrainingId: lt.id,
    chapterSequenceId: lt.chapterSequenceId || '',
    title: lt.training.title,
    duration: lt.training.duration || 0,
    regionName: lt.training.region?.name || '',
    highestUnlockedPosition,
    items: items.sort((a, b) => a.position - b.position),
    status: lt.status,
    skip: lt.status === 'completed' ? true : lt.skip || false,
    player: lt.player || 'standard',
    progress: lt.progress || 0,
    scorePercent: lt.scorePercent || 0,
    scorePassingPercent: lt.scorePassingPercent || 0,
    attemptStatus: lt.attemptStatus || '',
    policies: lt.policies as Array<Policy>,
    policiesForSignature: lt.policiesForSignature as Array<Policy>,
    policyAcknowledgement: lt.policyAcknowledgement || false,
    policyAcknowledgementSignature: lt.policyAcknowledgementSignature || false,
    companyContacts: lt.companyContacts as Array<CompanyContact>,

    auditoryFeedback: !!data.learnerTraining.auditoryFeedback,
    visualFeedback: !!data.learnerTraining.visualFeedback,

    certificateUrl: data.learnerTraining.certificateUrl || '',
    learnerPortalUrl: data.learnerTraining.learnerPortalUrl || '',

    // Settings
    textLocale: lt.textLocale || '',
    textLocales: lt.training.textLocales || [],
    subtitleLocale: lt.subtitleLocale || '',
    subtitleLocales: lt.training.subtitleLocales || [],
    supportedLanguages: createSupportedLanguages({
      textLocales: lt?.training?.textLocales || [],
      subtitleLocales: lt?.training?.subtitleLocales || [],
      mediumLocales: lt?.training?.mediumLocales || []
    }),
    languageAutoDetect: lt.training.languageAutoDetect || false,
    askName: data.learnerTraining.askName || false,
    autoPlay: data.learnerTraining.autoPlay || false,
    playbackSpeed: data.learnerTraining.playbackSpeed || 1,
    featureFocus: data.learnerTraining.featureFocus || false,
    fasterPlaybackSpeed: data.learnerTraining.fasterPlaybackSpeed || false,
    featureShowHotline: !!data.learnerTraining.featureShowHotline,
    featureShowReviewStep: !!data.learnerTraining.featureShowReviewStep,
    featureShowCertificate: !!data.learnerTraining.featureShowCertificate,
    featureShowSocialShare: !!data.learnerTraining.featureShowSocialShare,
    featureShowAbout: !!data.learnerTraining.featureShowAbout,
    featureShowLlama: !!data.learnerTraining.featureShowLlama,
    featureShowChatbot: !!data.learnerTraining.featureShowChatbot,
    featureShowNextTraining: !!data.learnerTraining.featureShowNextTraining,
    mediumLocale: lt.mediumLocale || '',
    mediumLocales: lt.training.mediumLocales || [],
    subtitles: lt.subtitles || false,
    featureShowIssueReporting: !!data.learnerTraining.featureShowIssueReporting,
    darkMode: data.learnerTraining.darkMode || 'automatic',
    onboarding: data.learnerTraining.onboarding,
    shouldDisplayCourseDurationCard:
      data.learnerTraining.shouldDisplayCourseDurationCard || false,
    shouldDisplayTimeRemainingPill:
      data.learnerTraining.shouldDisplayTimeRemainingPill || false,
    remainingDurationInMinutesForCourseTimers:
      data.learnerTraining.remainingDurationInMinutesForCourseTimers || null,
    chatMessages: data.learnerTraining.chatbotMessages,
    chatGreeting: data.learnerTraining.chatGreeting || '',
    chatQuestions: data.learnerTraining.chatQuestions || [],
    featureShowHomeButton: data.learnerTraining.featureShowHomeButton || true,
    showDemoBanner: data.learnerTraining.showDemoBanner || false,

    // User
    promptOptions: data.learnerTraining.promptOptions || [],
    promptProfile: data.learnerTraining.promptProfile || false,
    firstName: user.firstName || '',
    companyName: user.company.name || '',
    companyId: user.company.id || '',
    logoUrl: user.company.logoUrl || '',
    feedbackUrl: user.company.feedbackUrl || '',
    disclaimer: lt.disclaimer || undefined,

    languages: []
  };
}
